import { Security } from '../../proto/generated/security'
import { RootState } from '../../store'
import { calculateSnapshotDetails } from '../../util/security'
import { toLocaleStringIn } from '../../util/general'
import { green, red } from '../../assets/colors'
import { LiveMarketTextSelector } from '../common/LiveMarketText'

const displaySelectorCache = new Map<string, LiveMarketTextSelector>()
const colorSelectorCache = new Map<string, LiveMarketTextSelector>()

const getCachedSelector = (
    cache: Map<string, LiveMarketTextSelector>,
    cacheKey: string,
    factory: () => LiveMarketTextSelector
): LiveMarketTextSelector => {
    if (!cache.has(cacheKey)) {
        cache.set(cacheKey, factory())
    }
    return cache.get(cacheKey)!
}

export type IndexComponentInitialStats = {
    ltp: number | null
    changePercentage: number | null
    contribution: number | null
}

const computeIndexComponentStats = (
    state: RootState,
    secKey: string,
    security: Security,
    previousClose: number,
    spi: number
) => {
    const snapshot = state.securityStore.snapshots[secKey]
    if (!snapshot) return null
    const details = calculateSnapshotDetails(security, {
        ...snapshot,
        previousClose,
    })
    if (!details) return null
    const priceMultiplier = security.priceMultiplier ?? 100
    const rawChange = previousClose !== 0 ? snapshot.lastTradedPrice - previousClose : null
    const contribution = rawChange != null ? (spi * rawChange) / priceMultiplier : null
    return {
        ltp: details.ltp,
        ltpChange: details.ltpChange,
        changePercentage: details.changePercentage,
        contribution,
    }
}

export type IndexComponentLiveSelectors = {
    contribution: LiveMarketTextSelector
    changePercent: LiveMarketTextSelector
    ltp: LiveMarketTextSelector
    ltpColor: LiveMarketTextSelector
}

export const buildIndexComponentLiveSelectors = (
    secKey: string,
    security: Security,
    previousClose: number,
    spi: number,
    initial: IndexComponentInitialStats
): IndexComponentLiveSelectors => {
    const priceMultiplier = security.priceMultiplier ?? 100
    const prefix = `idx:${secKey}:${previousClose}:${spi}:${priceMultiplier}:${initial.ltp}:${initial.changePercentage}:${initial.contribution}`

    const contribution = getCachedSelector(displaySelectorCache, `${prefix}:contrib`, () => {
        let seeded = false
        return (state) => {
            const stats = computeIndexComponentStats(state, secKey, security, previousClose, spi)
            if (stats?.contribution != null) {
                seeded = true
                return `${stats.contribution.toFixed(2)} pts`
            }
            if (!seeded && initial.contribution != null) {
                return `${initial.contribution.toFixed(2)} pts`
            }
            return '0.00 pts'
        }
    })

    const changePercent = getCachedSelector(displaySelectorCache, `${prefix}:pct`, () => {
        let seeded = false
        return (state) => {
            const stats = computeIndexComponentStats(state, secKey, security, previousClose, spi)
            if (stats?.changePercentage != null && Number.isFinite(stats.changePercentage)) {
                seeded = true
                return `${stats.changePercentage.toFixed(2)}%`
            }
            if (!seeded && initial.changePercentage != null && Number.isFinite(initial.changePercentage)) {
                return `${initial.changePercentage.toFixed(2)}%`
            }
            return '-'
        }
    })

    const ltp = getCachedSelector(displaySelectorCache, `${prefix}:ltp`, () => {
        let seeded = false
        return (state) => {
            const stats = computeIndexComponentStats(state, secKey, security, previousClose, spi)
            if (stats?.ltp != null && Number.isFinite(stats.ltp)) {
                seeded = true
                return toLocaleStringIn(stats.ltp)
            }
            if (!seeded && initial.ltp != null && Number.isFinite(initial.ltp)) {
                return toLocaleStringIn(initial.ltp)
            }
            return '-'
        }
    })

    const ltpColor = getCachedSelector(colorSelectorCache, `${prefix}:color`, () => {
        let seeded = false
        return (state) => {
            const stats = computeIndexComponentStats(state, secKey, security, previousClose, spi)
            if (stats) {
                seeded = true
                return stats.ltpChange >= 0 ? green : red
            }
            if (!seeded && initial.ltp != null && initial.changePercentage != null) {
                return initial.changePercentage >= 0 ? green : red
            }
            return green
        }
    })

    return { contribution, changePercent, ltp, ltpColor }
}
